import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as actions from 'containers/ProjectTools/Notes/Note/actions';
import * as notesLandingActions from 'containers/ProjectTools/Notes/actions';
import { duplicateNote, duplicateNoteTemplate } from 'apollo/notes.query';
import { pushSiteNotification } from 'containers/App/actions';
import { createNoteTemplateFolder as actionCreateNoteTemplateFolder } from 'containers/Settings/Projects/actions';
import { deleteTemplate, deleteNote as deleteNoteAction } from 'containers/ProjectTools/Notes/Note/actions';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APModal from 'components/Modal/APModal';
import APResponsiveDropdown from 'components/Dropdown/APResponsiveDropdown';
import { streamDownload } from 'api/notes';
import { copyToClipboard, isFileImageExtension } from 'utils/general';
import { cipherID } from 'utils/idCipher';
import ShareModal from 'pages/Projects/Tools/Share/ShareModal';
import SharePlusModal from 'pages/Projects/Tools/Share/SharePlusModal';
import { downloadPdf, downloadImage } from '../NotePreview/NotePreview';
import SaveTemplateModal from '../Notes.SaveTemplateModal';
import { DeleteTemplateModal } from '../../BusinessSettings/Templates/Notes/NoteTemplate.OptionsButton';

/**
 * @description: ActionButton Component from leads index.
 * @param {*}
 * @return {*}
 */
export const NoteEditorActionButton = ({
  isTemplate,
  id,
  note,
}) => {
  const dispatch = useDispatch();

  // #TODO: Need to refactor these action bindings to avoid collisions between Note and Notes actions.
  const {
    deleteNote,
    deleteNoteTemplate,
    saveTemplate,
    createNoteTemplateFolder,
    pushNotification,
  } = bindActionCreators({
    ...actions,
    ...notesLandingActions,
    deleteNote: deleteNoteAction,
    deleteNoteTemplate: deleteTemplate,
    createNoteTemplateFolder: actionCreateNoteTemplateFolder,
    pushNotification: pushSiteNotification,
  }, dispatch);

  const projectId = useSelector((state) => state.projectTools.note.note?.weddingId);
  const orgId = useSelector((state) => state.projectTools.note.template?.organizationId);
  const templateFolders = useSelector((state) => state.settings.projectSettings.noteTemplateFolders);

  // #TODO: This can possibly be refactored into a reducer so it can be accessed
  // via global state rather than duplicating this logic where it is needed.
  // Share permissions.
  const { organization, permissions } = useSelector((state) => state.global.userInfo);
  const { features, organizationId: projectOrgId } = useSelector((state) => state.projectTools.project || {});
  // Need to check if is in org for both notes and templates.
  const isInOrg = organization && (projectOrgId === organization.id || orgId === organization.id);
  const isPlanningPartner = !organization || projectOrgId !== organization.id;
  const notesFeature = useMemo(
    () => Array.isArray(features) ?
      features.find(feat => feat.id === 'notes_enabled') : null,
    [features],
  );
  const designStudioFeature = useMemo(
    () => Array.isArray(features) ?
      features.find(feat => feat.id === 'design_studio_enabled') : null,
    [features],
  );
  const { features: featuresReleased } = useSelector((state) => state.projects.currentProject);
  const { designStudioReleased, notesReleased } = featuresReleased || {};
  const weddingPermission = useSelector((state) => state.projects.weddingPermission);
  const designStudioViewPermission = weddingPermission &&
    weddingPermission.find(p => p.name === 'design-studio.view-tool');
  const hasStudioShareViewingPermission = designStudioViewPermission ? designStudioViewPermission.value : false;
  const canViewDesignStudio = isInOrg || hasStudioShareViewingPermission && designStudioReleased;
  // Org owner/members of project can always share.
  // Only show share plus if notes is enabled and is released
  // or studio share is enabled and is released.
  // or is client and can view studio shares.
  const notesIsReleased = notesFeature?.checked && notesReleased;
  const showShare = isInOrg || notesIsReleased;
  const showSharePlus =  isInOrg || (
    designStudioFeature?.checked && isPlanningPartner && canViewDesignStudio && notesIsReleased
  );

  // Clients should never be able to save as template.
  // Cannot save as template if already a template.
  // Org members should not be able to save templates if they don't have access to templates.
  const showSaveAsTemplate = isInOrg && !isTemplate &&
    permissions && permissions['settings.template.edit'] && permissions['settings.template.view'];
  // Clients should never be able to duplicate or delete notes/templates.
  const showDuplicateButton = isInOrg;
  const showDeleteButton = isInOrg;

  const [showActionOptions, toggleActionDropdown] = useState(false);

  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [templateModalOpened, setTemplateModalOpened] = useState(false);

  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState(null);
  const [errorModalTitle, setErrorModalTitle] = useState(null);
  const [errorModalButtonTitle, setErrorModalButtonTitle] = useState(null);

  const [showShareModal, toggleShareModalOpen] = useState(false);
  const [showSharePlusModal, toggleSharePlusModalOpen] = useState(false);
  const [showDuplicateModal, toggleDuplicteModalOpen] = useState(false);

  const previewUrl = useMemo(() => {
    if (isTemplate) {
      const encryptedID = cipherID(id);
      return`${window.location.origin}/app/v2/note-template-viewer/${encryptedID}`;
    }
    const encryptedID = cipherID(note.id);
    return`${window.location.origin}/app/v2/note-viewer/${encryptedID}`;
  }, [note.id]);

  const content = isTemplate ? 'note template' : 'note';

  const show = () => {
    toggleActionDropdown(!showActionOptions);
  };

  const dismiss = () => {
    toggleActionDropdown(false);
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpened(false);
    setErrorModalMessage(null);
  };

  const renderDuplicateConfirm = () => {
    const confirmDuplicateNote = async (e) => {
      e.stopPropagation();
      if (isTemplate) {
        try {
          const response = await duplicateNoteTemplate({ id });
          const duplicatedTemplate = response?.data?.duplicateNoteTemplate?.noteTemplate;
          if (duplicatedTemplate) {
            window.open(
              `/app/v2/pro/${orgId}/settings/templates/notes-templates/${duplicatedTemplate.id}?edit=true`, '_self');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('note editor duplicate error happened,', err);
          toggleDuplicteModalOpen(false);
        }
      } else {
        try {
          const response = await duplicateNote({ id });
          const duplicatedNote = response?.data?.duplicateNote?.note;
          if (duplicatedNote) {
            window.open(`/app/project/${projectId}/tools/notes/${duplicatedNote.id}?edit=true`, '_self');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('note editor duplicate error happened,', err);
          toggleDuplicteModalOpen(false);
        }
      }
    };

    return (
      <APModal
        open={showDuplicateModal}
        width={350}
        height={175}
        onClose={() => toggleDuplicteModalOpen(false)}
        className="ap-modal--duplicate-confirmation"
        size="sm"
        hasHeader
        title={`Note${isTemplate ? ' Template' : ''} Duplicated`}
        actions={(
          <button
            type="button"
            className="button"
            onClick={confirmDuplicateNote}
          >
            Got It
          </button>
        )}
      >
        You'll now be dropped into your new {content}. Don't forget to rename this new {content} to avoid any confusion.
      </APModal>
    );
  };

  const copyLink = () => {
    if (note.url) {
      copyToClipboard(note.url);
    }
    else {
      copyToClipboard(previewUrl);
    }
    pushNotification({
      message: 'Note link copied to clipboard.',
      time: 3000,
    });
    dismiss();
  };

  const duplicateItem = (e) => {
    e.stopPropagation();
    toggleDuplicteModalOpen(true);
    dismiss();
  };

  const shareModalOpen = (e) => {
    e.stopPropagation();
    toggleShareModalOpen(true);
    dismiss();
  };

  const sharePlusModalOpen = (e) => {
    e.stopPropagation();
    toggleSharePlusModalOpen(true);
    dismiss();
  };

  const saveAsTemplate = (e) => {
    e.stopPropagation();
    setTemplateModalOpened(true);
    dismiss();
  };

  const deleteItem = (e) => {
    e.stopPropagation();
    setDeleteConfirmOpened(true);
    dismiss();
  };

  const download = (e) => {
    e.stopPropagation();

    // Directly download file if file is uploaded as the note.
    if (note.isFileUpload && note.fileViewerUrl && note.file) {
      const { originalFilename: filename } = note.file;
      const fileUrl = note.fileViewerUrl;

      if (isFileImageExtension(filename)) {
        downloadImage(fileUrl, filename);
      } else {
        downloadPdf(fileUrl, filename);
      }
      dismiss();
    } else {
      const params = isTemplate ? { note_template_id: id } : { note_id: id };
      setIsPreparingDownload(true);
      const payload = isTemplate ? {
        orgId: note.organizationId,
        params,
      } : {
        weddingId: note.weddingId,
        params,
      };

      streamDownload(payload)
        .then((response) => {
          if (response.status !== 401) window.location.replace(response.data.url);
          setIsPreparingDownload(false);
          dismiss();
        })
        .catch(() => {
          setIsPreparingDownload(false);
          setErrorModalOpened(true);
          setErrorModalTitle('Something went wrong.');
          setErrorModalMessage("Sorry, we couldn't prepare the download. Please try again.");
          setErrorModalButtonTitle('Close');
        });
    }
  };

  const renderActionMenu = () => {
    return (
      <>
        <button
          className={clsx(
            'action_buttons__button',
            'button--unstyled',
            'button--menu',
          )}
          onClick={() => {
            window.open(previewUrl, '_blank');
          }}
        >
          Preview
        </button>
        {!isTemplate && (
          <>
            {showShare && (
              <>
                <button
                  className={clsx(
                    'action_buttons__button',
                    'button--unstyled',
                    'button--menu',
                  )}
                  onClick={copyLink}
                >
                  Copy Link
                </button>
                <button
                  className={clsx(
                    'action_buttons__button',
                    'button--unstyled',
                    'button--menu',
                  )}
                  onClick={shareModalOpen}
                >
                  Share This Note
                </button>
              </>
            )}
            {showSharePlus && (
              <button
                className={clsx(
                  'action_buttons__button',
                  'button--unstyled',
                  'button--menu',
                )}
                onClick={sharePlusModalOpen}
              >
                Share +
              </button>
            )}
          </>
        )}
        {showDuplicateButton && (
          <button
            className={clsx(
              'action_buttons__button',
              'button--unstyled',
              'button--menu',
            )}
            onClick={duplicateItem}
          >
            Duplicate
          </button>
        )}
        {showSaveAsTemplate && (
          <button
            className={clsx(
              'action_buttons__button',
              'button--unstyled',
              'button--menu',
            )}
            onClick={saveAsTemplate}
          >
            Save as Template
          </button>
        )}
        {showDeleteButton && (
          <button
            className={clsx(
              'action_buttons__button',
              'button--unstyled',
              'button--menu',
            )}
            onClick={deleteItem}
          >
            Delete
          </button>
        )}
        <button
          className={clsx(
            'action_buttons__button',
            'button--unstyled',
            'button--menu',
            'border-top',
            'download-link',
            isPreparingDownload ? 'button--add loading' : '',
          )}
          onClick={download}
        >
          Download
        </button>
      </>
    );
  };

  const trigger = (
    <DropdownButton
      className="button button--md button--action"
    >
      <span className="visually-hidden--mobile">Actions</span>
      {showActionOptions ? <MdKeyboardArrowUp size={16} /> : <MdKeyboardArrowDown size={16} />}
    </DropdownButton>
  );

  return (
    <>
      <APResponsiveDropdown
        opened={showActionOptions}
        show={show}
        dismiss={dismiss}
        triggerButton={trigger}
      >
        {renderActionMenu()}
      </APResponsiveDropdown>
      <DeleteTemplateModal
        opened={deleteConfirmOpened}
        setOpened={setDeleteConfirmOpened}
        id={id}
        isShortcut={note?.isShortcut}
        deleteNoteTemplate={isTemplate ? deleteNoteTemplate : deleteNote}
        isTemplate={isTemplate}
        actionUrl={isTemplate
          ? `/app/pro/${orgId}/settings/templates/notes-templates`
          : `/app/project/${projectId}/tools/notes`}
      />
      {renderDuplicateConfirm()}
      {templateModalOpened && (
        <SaveTemplateModal
          note={note}
          templateModalOpened={templateModalOpened}
          setTemplateModalOpened={setTemplateModalOpened}
          templateFolders={templateFolders}
          saveTemplate={saveTemplate}
          createNoteTemplateFolder={createNoteTemplateFolder}
        />
      )}
      {errorModalOpened && (
        <APModal
          open={errorModalOpened}
          width={620}
          height={300}
          onClose={errorModalCloseHandler}
          className="ap-modal--note-options-confirmation"
          size="narrow"
          title={errorModalTitle}
          actions={(
            <button
              onClick={errorModalCloseHandler}
              className="button"
            >
              {errorModalButtonTitle}
            </button>
          )}
        >
          {errorModalMessage}
        </APModal>
      )}
      {showShareModal && (
        <ShareModal
          open={showShareModal}
          onClose={() => toggleShareModalOpen(false)}
          note={note}
        />
      )}
      {showSharePlusModal && (
        <SharePlusModal
          open={showSharePlusModal}
          onClose={() => toggleSharePlusModalOpen(false)}
          currentNote={note}
        />
      )}
    </>
  );
};
